import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserSubscribePlanThunk as onGetUserSubscribePlanThunk } from "../../../slices/users/thunk";
import { getFileUrl } from "../../../utils/GetAuthUser";
import { Header } from "../../../components/header/Header";

const SubscribePlan = () => {
  const dispatch = useDispatch();
  const fileUrl = getFileUrl();

  // Local state for dropdown
  const [selectedUserType, setSelectedUserType] = useState("");

  // Selector
  const selectUserState = (state) => state.user;
  const userSelector = createSelector(selectUserState, (user) => ({
    isLoading: user.isLoading,
    userSubscribePlanRevenue: user.userSubscribePlanRevenue || [],
  }));

  const { isLoading, userSubscribePlanRevenue } = useSelector(userSelector);

  // Fetch data initially (no filter)
  useEffect(() => {
    dispatch(onGetUserSubscribePlanThunk({ userType: null }));
  }, [dispatch]);

  // Handle dropdown change
  const handleUserTypeChange = (e) => {
    const value = e.target.value;
    setSelectedUserType(value);

    // Determine userType param
    let userType = null;
    if (value === "1") userType = 1; // User
    else if (value === "2") userType = 2; // Artist
    else userType = null; // All

    // Dispatch with selected type
    dispatch(onGetUserSubscribePlanThunk({ userType }));
  };

  return (
    <>
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-14 md:h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color">
        <Header title="Subscribe Plans" showBack={true} />
      </header>

      <div className="w-full xl:max-[70%] flex flex-col border border-border_color rounded-[14px] p-5 mt-4 ">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <span className="text-lg sm:text-[22px] leading-7 tracking-wider text-black font-bold font-grotesk">
            Subscribe Plans
          </span>

          <span className="flex gap-3">
            <select
              value={selectedUserType}
              onChange={handleUserTypeChange}
              className="w-full min-w-36 px-3 py-[14px] bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            >
              <option value="">All</option>
              <option value="1">User</option>
              <option value="2">Artist</option>
            </select>
          </span>
        </div>

        {/* Table */}
        <div
          className="overflow-x-auto overflow-y-auto"
          style={{ maxHeight: "calc(85vh - 120px)" }}
        >
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-border_color">
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                  Sr No.
                </th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                  User
                </th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                  User Type
                </th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                  Plan
                </th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                  Plan Amount
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr
                    key={`skeleton-${index}`}
                    className={(index + 1) % 2 === 0 ? "bg-[#2878870F]" : "bg-transparent"}
                  >
                    {/* Sr# skeleton */}
                    <td className="px-3 py-3 first:rounded-l-lg last:rounded-r-lg bg-transparent">
                      <div
                        className={`h-7 w-7 rounded-lg ${(index + 1) % 2 === 0 ? "bg-white" : "bg-[#2878870F]"} animate-pulse`}
                      />
                    </td>

                    {/* User skeleton */}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse" />
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                      </div>
                    </td>

                    {/* User Type skeleton */}
                    <td className="px-3 py-3">
                      <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                    </td>

                    {/* Plan Name skeleton */}
                    <td className="px-3 py-3">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>

                    {/* Plan Amount skeleton */}
                    <td className="px-3 py-3">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : userSubscribePlanRevenue && userSubscribePlanRevenue.length > 0 ? (
                userSubscribePlanRevenue.map((plan, index) => (
                  <tr
                    key={plan.id || index}
                    className={
                      (index + 1) % 2 === 0
                        ? "bg-[#2878870F]"
                        : "bg-transparent"
                    }
                  >
                    {/* Sr# */}
                    <td className="px-3 py-3 first:rounded-l-lg last:rounded-r-lg bg-transparent">
                      <div
                        className={`h-7 w-7 rounded-lg flex items-center justify-center text-sm tracking-wider font-medium font-grotesk text-black ${
                          (index + 1) % 2 === 0 ? "bg-white" : "bg-[#2878870F]"
                        }`}
                      >
                        {index + 1}
                      </div>
                    </td>

                    {/* User with image */}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-200">
                          <img
                            src={
                              plan.image
                                ? `${fileUrl}/${plan.image}`
                                : "/default-avatar.png"
                            }
                            alt="User"
                            className="w-full h-full object-cover"
                            onError={(e) =>
                              (e.target.src = "/default-avatar.png")
                            }
                          />
                        </div>
                        <span className="text-sm tracking-wider font-medium font-grotesk text-black">
                          {plan.name || "Unknown"}
                        </span>
                      </div>
                    </td>

                    {/* User Type */}
                    <td className="px-3 py-3 text-sm tracking-wider font-medium font-grotesk text-black">
                      {plan.userType || "N/A"}
                    </td>

                    {/* Plan Name */}
                    <td className="px-3 py-3 text-sm tracking-wider font-medium font-grotesk text-black">
                      {plan.planName || "N/A"}
                    </td>

                    {/* Plan Amount */}
                    <td className="px-3 py-3 text-sm tracking-wider font-medium font-grotesk text-black first:rounded-l-lg last:rounded-r-lg bg-transparent">
                      ${plan.cost || "0.00"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="py-6 text-center text-xl tracking-wider font-bold font-grotesk text-gray-500"
                  >
                    No Plans Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default SubscribePlan;
